import { useEffect, useRef, useState } from "react";
import { useT } from "@reciperoll/core";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { AuthLogo } from "./AuthLogo";

// Read a Supabase auth-redirect error from the URL (query string or hash).
export function readAuthError(): string | null {
  const q = new URLSearchParams(window.location.search);
  const h = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const desc = q.get("error_description") || h.get("error_description");
  const err = q.get("error") || h.get("error");
  if (desc) return decodeURIComponent(desc.replace(/\+/g, " "));
  return err;
}

// Landing screen for the email-confirmation link (and any auth-redirect error).
// Supabase's detectSessionInUrl exchanges the code on load and establishes the
// session; this screen shows the confirming/confirmed/error states, then hands
// off to the app.
export function AuthCallback() {
  const t = useT();
  const { session, loading } = useAuth();
  const [error, setError] = useState(readAuthError);

  // Clean the URL back to "/" — a full navigation so AuthGate re-evaluates with a
  // plain path (and a persisted session, if the confirmation succeeded).
  const goHome = () => window.location.replace("/");

  // Confirm the account from the link. token_hash (not device-bound) is preferred;
  // legacy PKCE `?code=` links are exchanged by detectSessionInUrl automatically.
  const handled = useRef(false);
  useEffect(() => {
    if (handled.current) return;
    const params = new URLSearchParams(window.location.search);
    const token_hash = params.get("token_hash");
    const type = params.get("type");
    if (token_hash && type) {
      handled.current = true;
      supabase.auth
        .verifyOtp({ token_hash, type: type as never })
        .then(({ error }) => error && setError(error.message));
    }
  }, []);

  // On success, continue into the app automatically after a beat.
  useEffect(() => {
    if (!error && !loading && session) {
      const t = setTimeout(goHome, 1400);
      return () => clearTimeout(t);
    }
  }, [error, loading, session]);

  const btn =
    "mt-6 w-full rounded-lg bg-terracotta px-4 py-2.5 font-semibold text-white transition hover:bg-terracotta-dark";
  const heading = "text-xl font-bold text-ink";
  const body = "mt-2 text-sm text-ink-soft";

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper p-4">
      <div className="w-full max-w-sm rounded-2xl bg-surface p-8 text-center shadow-sm ring-1 ring-line">
        <AuthLogo />

        {error ? (
          <>
            <h1 className={`mt-4 ${heading}`}>{t("auth.linkDidntWork")}</h1>
            <p className={body}>{error}</p>
            <p className={body}>{t("auth.confirmLinkExpired")}</p>
            <button onClick={goHome} className={btn}>
              {t("auth.backToSignIn")}
            </button>
          </>
        ) : loading ? (
          <p className={`mt-4 ${body}`}>{t("auth.confirmingEmail")}</p>
        ) : session ? (
          <>
            <h1 className={`mt-4 ${heading}`}>{t("auth.emailConfirmed")}</h1>
            <p className={body}>{t("auth.emailConfirmedBody")}</p>
            <button onClick={goHome} className={btn}>
              {t("common.continue")}
            </button>
          </>
        ) : (
          <>
            <h1 className={`mt-4 ${heading}`}>{t("auth.almostThere")}</h1>
            <p className={body}>{t("auth.almostThereBody")}</p>
            <button onClick={goHome} className={btn}>
              {t("auth.backToSignIn")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
